import { trackCustomWarning } from './datadog-rum';
import { setToLocalStorage } from './utils/set-to-local-storage';

const ONETRUST_POLL_INTERVAL_MS = 100;
const ONETRUST_POLL_TIMEOUT_MS = 10_000;
/**
 * Lets in-flight async work scheduled by other consent listeners settle
 * before the page tears down. Most relevant: the headless Shopify consent
 * bridge's `setTrackingConsent` callback (~150ms debounce + a callback
 * round-trip) and any final Segment beacons. 500ms covers those without
 * keeping device-mode SDKs alive much longer than necessary.
 */
const RELOAD_DELAY_MS = 500;

/**
 * Sentinel localStorage key used to fan a consent revoke out to sibling tabs.
 * OneTrust's OnConsentChanged callback is tab-local, so without this, only
 * the tab that opened the banner would unload its device-mode SDKs while
 * other tabs of the same origin keep leaking until natural navigation.
 *
 * `storage` events fire in OTHER tabs only, never the writer's tab — exactly
 * the fanout we need.
 */
export const CROSS_TAB_RELOAD_KEY = '__seed_consent_reload';

export const CONSENT_RELOAD_TRIGGERED = 'consent_reload_triggered';
export const CONSENT_RELOAD_SKIPPED_ROUTE = 'consent_reload_skipped_route';
export const CONSENT_RELOAD_REGISTER_FAILED = 'consent_reload_register_failed';
export const CROSS_TAB_ORIGIN = 'cross_tab';

/**
 * Routes where a mid-session reload would destroy in-progress user state
 * (form fields, checkout progress, quiz answers). On these routes we skip
 * the reload and emit telemetry instead, accepting the device-mode-SDK leak
 * there in exchange for not breaking the user's flow. Cart is intentionally
 * NOT in this list because cart state is persisted to localStorage and the
 * upstream cart API.
 */
const RELOAD_BLOCKED_ROUTE_PATTERNS: RegExp[] = [
  /^\/account(?:\/|$)/,
  /^\/checkout(?:\/|$)/,
  /^\/login(?:\/|$)/,
  /^\/onboarding(?:\/|$)/,
  /^\/seed-onboarding(?:\/|$)/,
  /^\/reset-password(?:\/|$)/,
  /^\/recover(?:\/|$)/,
  /^\/verify-account(?:\/|$)/,
  /^\/continue(?:\/|$)/,
  /^\/is-[a-z0-9]+-right-for-you(?:\/|$)/,
  /^\/is-ds01-right-for-you-glp1(?:\/|$)/,
  /^\/surveys?(?:\/|$)/,
];

let initialized = false;
let lastSnapshot: string | null = null;
let pendingReloadTimer: ReturnType<typeof setTimeout> | null = null;
let consentChangedRegistered = false;

/**
 * Thin indirection so tests can intercept reload without needing to mock
 * `window.location.reload` directly (jsdom ≥ 20 marks it non-configurable
 * and non-writable).
 * @internal — only consumed by tests.
 */
export const __reloadPage = { fn: () => window.location.reload() };

export function __resetConsentReloadStateForTests(): void {
  initialized = false;
  lastSnapshot = null;
  if (pendingReloadTimer !== null) {
    clearTimeout(pendingReloadTimer);
    pendingReloadTimer = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('storage', handleCrossTabReload);
  }
  // OneTrust does not provide a method to unregister OnConsentChanged callbacks.
  // The consentChangedRegistered flag guards against double-registration when
  // tests call initializeConsentReload() -> __resetConsentReloadStateForTests()
  // -> initializeConsentReload() again.
  consentChangedRegistered = false;
}

/**
 * Schedules a single reload. Idempotent within the delay window — rapid
 * consent toggles or duplicate cross-tab signals collapse to one reload.
 * The route check inside the timer is load-bearing: the user may have
 * navigated onto a blocked route during the 500ms wait.
 */
function scheduleReload(): void {
  if (pendingReloadTimer !== null) return;
  pendingReloadTimer = setTimeout(() => {
    pendingReloadTimer = null;
    if (isReloadBlockedRoute()) return;
    __reloadPage.fn();
  }, RELOAD_DELAY_MS);
}

export function getActiveGroups(): string[] {
  const raw = window.OnetrustActiveGroups || '';
  return raw
    .split(',')
    .map((g: string) => g.trim())
    .filter(Boolean);
}

function isReloadBlockedRoute(): boolean {
  return RELOAD_BLOCKED_ROUTE_PATTERNS.some((re) =>
    re.test(window.location.pathname),
  );
}

/**
 * On revoke (any category granted -> denied), reload the page so that
 * already-loaded device-mode SDKs (Hotjar, Meta, Snap, TikTok, Pinterest,
 * Bing UET, etc.) unload. Per Segment's documented limitation: once a
 * device-mode third-party library is loaded, it cannot be unloaded; the
 * Segment wrapper can stop forwarding new events but cannot stop the SDK's
 * own internal data collection.
 *
 * https://www.twilio.com/docs/segment/privacy/consent-management/onetrust-wrapper
 */
function onConsentChanged(): void {
  const next = getActiveGroups();
  const nextKey = next.join(',');

  if (lastSnapshot === null) {
    lastSnapshot = nextKey;
    return;
  }

  const prev = lastSnapshot.split(',').filter(Boolean);
  lastSnapshot = nextKey;

  const revoked = prev.filter((c) => !next.includes(c));
  if (revoked.length === 0) return;

  if (isReloadBlockedRoute()) {
    trackCustomWarning(CONSENT_RELOAD_SKIPPED_ROUTE, {
      pathname: window.location.pathname,
      revoked: revoked.join(','),
    });
    return;
  }

  if (pendingReloadTimer !== null) return;

  trackCustomWarning(CONSENT_RELOAD_TRIGGERED, {
    revoked: revoked.join(','),
  });
  notifyOtherTabs();
  scheduleReload();
}

function notifyOtherTabs(): void {
  setToLocalStorage(CROSS_TAB_RELOAD_KEY, String(Date.now()));
}

function handleCrossTabReload(e: StorageEvent): void {
  if (e.key !== CROSS_TAB_RELOAD_KEY || e.newValue == null) return;

  if (isReloadBlockedRoute()) {
    trackCustomWarning(CONSENT_RELOAD_SKIPPED_ROUTE, {
      pathname: window.location.pathname,
      origin: CROSS_TAB_ORIGIN,
    });
    return;
  }

  if (pendingReloadTimer !== null) return;

  trackCustomWarning(CONSENT_RELOAD_TRIGGERED, { origin: CROSS_TAB_ORIGIN });
  scheduleReload();
}

function isOneTrustReady(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    typeof window.OneTrust?.OnConsentChanged === 'function' &&
    typeof window.OnetrustActiveGroups === 'string'
  );
}

function waitForOneTrust(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (isOneTrustReady()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (isOneTrustReady()) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
      }
    }, ONETRUST_POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve(false);
    }, ONETRUST_POLL_TIMEOUT_MS);
  });
}

/**
 * Safe to call multiple times; only the first call performs setup. Quiet
 * no-op when OneTrust never loads.
 */
export async function initializeConsentReload(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (initialized) return;
  initialized = true;

  const ready = await waitForOneTrust();
  if (!ready) return;

  window.addEventListener('storage', handleCrossTabReload);

  lastSnapshot = getActiveGroups().join(',');
  if (!consentChangedRegistered) {
    try {
      window.OneTrust!.OnConsentChanged(onConsentChanged);
      consentChangedRegistered = true;
    } catch (err) {
      trackCustomWarning(CONSENT_RELOAD_REGISTER_FAILED, {
        error: String(err),
      });
    }
  }
}
